"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function TheStarshipVision() {
  const {
    ref: imgRef,
    isVisible: imgVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const noiseSvg = `data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.15'/%3E%3C/svg%3E`;
  return <section data-section-id="97127" id="the-story-of-rpg-studios" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none bg-fixed">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,_hsl(250_70%_15%_/_0.5),_transparent_50%)] mix-blend-screen" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,_hsl(180_70%_20%_/_0.4),_transparent_50%)] mix-blend-screen" />
        <div className="absolute inset-0 bg-background/90 backdrop-blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          
          {}
          <div ref={imgRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${imgVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/80 ring-1 ring-primary/20 group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1600169/img/quitman_ga_landscape.png" alt="RPG-Studios in Quitman, GA" className="w-full aspect-[4/5] object-cover grayscale contrast-110 brightness-75 transition-transform duration-700 group-hover:scale-105" />
              {}
              <div className="absolute inset-0 pointer-events-none mix-blend-overlay" style={{
              backgroundImage: `url("${noiseSvg}")`
            }} />
              {}
              <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-primary/30 pointer-events-none" />
            </div>
          </div>

          {}
          <div ref={textRef} className={`lg:col-span-7 space-y-10 transition-all duration-1000 delay-300 ease-out ${textVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary border border-primary/20 text-xs font-mono tracking-widest uppercase">
                <Sparkles className="size-4" />
                <span>Our Partnership Philosophy</span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-foreground">
                The Story of RPG-Studios
              </h2>
            </div>

            <blockquote className="text-2xl md:text-3xl font-serif italic text-muted-foreground leading-[1.6] border-l-4 border-primary pl-6">
              "Building relationships, not just businesses."
            </blockquote>

            <Separator className="bg-gradient-to-r from-primary via-secondary to-transparent h-[2px] w-full max-w-md opacity-80 border-0" />

            <div className="space-y-6 text-lg text-muted-foreground leading-[1.8] max-w-2xl font-sans">
              <p>Based in South Georgia, RPG-Studios was born from a desire to bridge the gap between complex technology and real-world business growth. We believe that a digital presence should be a source of confidence, not a point of confusion.</p>
              <p>
                Our journey is defined by the success of our partners. By focusing on long-term relationships rather than one-off transactions, we provide the strategic SEO, premium WordPress hosting, and social media growth that allows local businesses to thrive in a global digital economy.
              </p>
            </div>

            <div className="pt-4">
              <Link to="/ServicesPage">
                <Button size="lg" className="rounded-md px-8 h-14 text-base font-serif font-semibold group bg-gradient-to-r from-primary to-secondary hover:shadow-[0_0_20px_rgba(180,70,50,0.4)] transition-all">
                  View Our Services
                  <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>

        </div>
      </div>
    </section>;
}